import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { Button, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';

import useDentoloClaims from '../../useDentoloClaims';
import { BackButton, PageTitle } from '../../../../components/common';

import {
  usePrefillSomeFormFieldsForExistingDentoloClaim,
  useUploadTreatmentDocumentDentoloBackButtonLink,
  useUploadTreatmentDocumentDentoloNextButtonLink,
} from './uploadTreatmentDocumentDentoloHooks';
import {
  DENTOLO_DOCUMENT_TYPE,
  DENTOLO_DOCUMENT_TYPE_INVOICE,
  DENTOLO_IS_EXISTING_CLAIM_ANSWER,
  DENTOLO_TREATMENT_CATEGORY,
  useNewDentoloFormDataValue,
} from '../../claimFormData/newClaimFormData';

import { RightListItemIconDentolo, WrongListItemIcon } from '../../../../components/common/Icon';
import ButtonContainer from '../../../../components/ButtonContainer';

import FileUploader from './FileUploader';
import FileList from './FileList';
import DentoloDocumentIllustration from './DentoloDocumentIllustration';

import {
  StyledUploadDocumentHintContainer,
  StyledUploadDocumentHintDesktopIllustrationContainer,
  StyledUploadDocumentHintMainInformationContainer,
  StyledUploadDocumentHintMobileIllustrationContainer,
  StyledUploadDocumentHintsList,
  StyledUploadDocumentHintsListInnerContainer,
  StyledUploadDocumentHintsListOuterContainer,
  StyledUploadDocumentsHintBadStuffTitle,
  StyledVerticalContent,
} from './FileUploader.styled';

export default function UploadTreatmentDocumentDentolo(props) {
  const { selectedDocuments, setSelectedDocuments, setClaimSubmitted, claimsCount } = props;

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('desktop'));

  const validSelectedDocuments = Array.isArray(selectedDocuments)
    ? selectedDocuments.filter((d) => d?.valid)
    : [];

  const history = useHistory();
  const { claimId } = useParams();

  const [submitDentoloClaim, { loading: submitting }] = useDentoloClaims(claimId);

  const nextButtonLink = useUploadTreatmentDocumentDentoloNextButtonLink(claimId);

  const addFileHandler = (currFiles) => {
    setSelectedDocuments((state) => [...state, ...currFiles]);
  };

  const removeFileHandler = (ev, id) => {
    if (ev) {
      ev.preventDefault();
      ev.stopPropagation();
    }

    const updatedFileList = selectedDocuments?.filter((item) => item?.fileId !== id);
    setSelectedDocuments(updatedFileList);
  };

  const nextStepHandler = () => {
    history.push(nextButtonLink);
  };

  const handleSubmitClaim = () => {
    const payload = {
      files: validSelectedDocuments.map((doc) => doc.file),
    };

    submitDentoloClaim({
      payload,
      setSelectedDocuments,
      setClaimSubmitted,
      clearFormData: false,
    });
  };

  const hasFiles = selectedDocuments?.length > 0;

  const [treatmentCategory, setTreatmentCategory] = useNewDentoloFormDataValue(
    DENTOLO_TREATMENT_CATEGORY
  );
  const [documentType, setDocumentType] = useNewDentoloFormDataValue(DENTOLO_DOCUMENT_TYPE);
  const [isExistingClaim] = useNewDentoloFormDataValue(DENTOLO_IS_EXISTING_CLAIM_ANSWER);

  usePrefillSomeFormFieldsForExistingDentoloClaim(claimId, setTreatmentCategory, setDocumentType);

  const backButtonLink = useUploadTreatmentDocumentDentoloBackButtonLink(
    claimId,
    treatmentCategory,
    documentType,
    claimsCount,
    isExistingClaim
  );

  const shouldSubmitForm = !nextButtonLink;

  return (
    <>
      <BackButton
        label={
          <Typography variant="h3">
            <FormattedMessage id="common.button.back" />
          </Typography>
        }
        to={backButtonLink}
      />

      <PageTitle>
        <Typography variant="h1">
          <FormattedMessage
            id={
              hasFiles
                ? 'new_claim.upload.page.title.with.files'
                : 'new_claim.upload.page.title.without.files'
            }
          />
        </Typography>
      </PageTitle>

      <StyledVerticalContent>
        {!hasFiles && (
          <Typography variant="p">
            <FormattedMessage id="new_claim.upload.page.description" />
          </Typography>
        )}

        {hasFiles && (
          <Paper>
            <FileList files={selectedDocuments} onRemove={removeFileHandler} />
          </Paper>
        )}

        <Paper>
          <FileUploader selectedFiles={selectedDocuments} setSelectedFiles={addFileHandler} />

          {isDesktop && hasFiles && (
            <ButtonContainer>
              {shouldSubmitForm ? (
                <Button
                  onClick={handleSubmitClaim}
                  disabled={submitting || !validSelectedDocuments.length}
                >
                  <Typography variant="buttons">
                    <FormattedMessage id="dentolo.new_claim.submit_claim_form.send_button" />
                  </Typography>
                </Button>
              ) : (
                <Button
                  onClick={nextStepHandler}
                  disabled={!validSelectedDocuments.length}
                  data-testid="next_button"
                >
                  <Typography variant="buttons">
                    <FormattedMessage id="common.button.next" />
                  </Typography>
                </Button>
              )}
            </ButtonContainer>
          )}
        </Paper>

        {documentType === DENTOLO_DOCUMENT_TYPE_INVOICE && !hasFiles && (
          <Paper>
            <StyledUploadDocumentHintContainer>
              <StyledUploadDocumentHintMainInformationContainer>
                <Typography variant="p">
                  <FormattedMessage id="new_claim.upload.page.hint.description" />
                </Typography>

                <StyledUploadDocumentHintsListOuterContainer>
                  <StyledUploadDocumentHintsListInnerContainer>
                    <StyledUploadDocumentHintsList>
                      <RightListItemIconDentolo />
                      <Typography variant="p">
                        <FormattedMessage id="new_claim.upload.page.hint.good_stuff_dental_1" />
                      </Typography>
                    </StyledUploadDocumentHintsList>

                    <StyledUploadDocumentHintsList>
                      <RightListItemIconDentolo />
                      <Typography variant="p">
                        <FormattedMessage id="new_claim.upload.page.hint.good_stuff_2" />
                      </Typography>
                    </StyledUploadDocumentHintsList>

                    <StyledUploadDocumentHintsList>
                      <RightListItemIconDentolo />
                      <Typography variant="p">
                        <FormattedMessage id="new_claim.upload.page.hint.good_stuff_3" />
                      </Typography>
                    </StyledUploadDocumentHintsList>

                    <StyledUploadDocumentHintsList>
                      <RightListItemIconDentolo />
                      <Typography variant="p">
                        <FormattedMessage id="new_claim.upload.page.hint.good_stuff_4" />
                      </Typography>
                    </StyledUploadDocumentHintsList>

                    <StyledUploadDocumentsHintBadStuffTitle>
                      <Typography variant="p">
                        <FormattedMessage id="new_claim.upload.page.hint.bad_stuff_title" />
                      </Typography>
                    </StyledUploadDocumentsHintBadStuffTitle>

                    <StyledUploadDocumentHintsList>
                      <WrongListItemIcon />
                      <Typography variant="p">
                        <FormattedMessage id="new_claim.upload.page.hint.bad_stuff_1" />
                      </Typography>
                    </StyledUploadDocumentHintsList>

                    <StyledUploadDocumentHintsList>
                      <WrongListItemIcon />
                      <Typography variant="p">
                        <FormattedMessage id="new_claim.upload.page.hint.bad_stuff_2" />
                      </Typography>
                    </StyledUploadDocumentHintsList>

                    <StyledUploadDocumentHintsList>
                      <WrongListItemIcon />
                      <Typography variant="p">
                        <FormattedMessage id="new_claim.upload.page.hint.bad_stuff_3" />
                      </Typography>
                    </StyledUploadDocumentHintsList>
                  </StyledUploadDocumentHintsListInnerContainer>

                  <StyledUploadDocumentHintMobileIllustrationContainer>
                    <DentoloDocumentIllustration />
                  </StyledUploadDocumentHintMobileIllustrationContainer>
                </StyledUploadDocumentHintsListOuterContainer>
              </StyledUploadDocumentHintMainInformationContainer>

              <StyledUploadDocumentHintDesktopIllustrationContainer>
                <DentoloDocumentIllustration />
              </StyledUploadDocumentHintDesktopIllustrationContainer>
            </StyledUploadDocumentHintContainer>
          </Paper>
        )}
      </StyledVerticalContent>

      {!isDesktop && hasFiles && (
        <ButtonContainer>
          {shouldSubmitForm ? (
            <Button
              onClick={handleSubmitClaim}
              disabled={submitting || !validSelectedDocuments.length}
            >
              <Typography variant="buttons">
                <FormattedMessage id="dentolo.new_claim.submit_claim_form.send_button" />
              </Typography>
            </Button>
          ) : (
            <Button
              onClick={nextStepHandler}
              disabled={!validSelectedDocuments.length}
              data-testid="next_button"
            >
              <Typography variant="buttons">
                <FormattedMessage id="common.button.next" />
              </Typography>
            </Button>
          )}
        </ButtonContainer>
      )}
    </>
  );
}

UploadTreatmentDocumentDentolo.propTypes = {
  selectedDocuments: PropTypes.arrayOf(
    PropTypes.shape({
      fileId: PropTypes.string,
      valid: PropTypes.bool,
    })
  ),
  setSelectedDocuments: PropTypes.func,
};
